import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { 
  QrCode, Search, User, Mail, Phone, Calendar, CreditCard, 
  Syringe, CalendarPlus, CheckCircle, Clock, ArrowRight, Trash2
} from "lucide-react";
import { trackCheckIn } from "@/utils/metaPixel";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import QRCode from "react-qr-code";

const PIPELINE_STAGES = [
  { value: 'lead', label: 'Lead', color: 'bg-gray-500', icon: User },
  { value: 'tox_pass_activated', label: 'Tox Pass Activated', color: 'bg-blue-500', icon: QrCode },
  { value: 'consultation_booked', label: 'Consultation Booked', color: 'bg-purple-500', icon: Calendar },
  { value: 'payment_processed', label: 'Payment Processed', color: 'bg-amber-500', icon: CreditCard },
  { value: 'first_treatment_complete', label: 'First Treatment', color: 'bg-green-500', icon: Syringe },
  { value: 'next_appointment_booked', label: 'Next Appointment', color: 'bg-teal-500', icon: CalendarPlus },
];

interface Membership {
  id: string;
  member_number: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string | null;
  tier: string;
  status: string;
  pipeline_stage: string | null;
  visits_count: number;
  last_visit_at: string | null;
  valid_from: string;
  payment_amount: number | null;
  payment_date: string | null;
  first_treatment_date: string | null;
  next_appointment_date: string | null;
  discount_code: string | null;
  location_id: string | null;
  locations?: {
    name: string;
    city: string;
    state: string;
  } | null;
}

export const MembershipPipeline = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMember, setSelectedMember] = useState<Membership | null>(null);
  const [qrLookupNumber, setQrLookupNumber] = useState("");
  const [verifyingQr, setVerifyingQr] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: memberships = [], isLoading } = useQuery({
    queryKey: ['admin-memberships'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('memberships')
        .select(`
          *,
          locations:location_id (
            name,
            city,
            state
          )
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as Membership[];
    },
  });

  const updateMembershipMutation = useMutation({
    mutationFn: async (updates: Partial<Membership> & { id: string }) => {
      const { id, locations, ...data } = updates;
      const { error } = await supabase
        .from('memberships')
        .update(data)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-memberships'] });
      toast({ title: "Success", description: "Membership updated" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update membership", variant: "destructive" });
    },
  });

  const deleteMembershipMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('memberships')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-memberships'] });
      toast({ title: "Success", description: "Membership deleted" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete membership", variant: "destructive" });
    },
  });

  const handleVerifyQr = async () => {
    if (!qrLookupNumber.trim()) return;
    
    setVerifyingQr(true);
    try {
      const { data, error } = await supabase.functions.invoke('verify-tox-pass', {
        body: { memberNumber: qrLookupNumber.trim(), action: 'verify' }
      });
      
      if (error) throw error;
      
      if (data.valid) {
        const member = memberships.find(m => m.member_number === qrLookupNumber.trim());
        if (member) {
          setSelectedMember(member);
        }
        toast({ title: "Valid Member", description: `${data.member.customerName} - ${data.member.tier}` });
      } else {
        toast({ title: "Invalid", description: data.error || "Member not found", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to verify QR code", variant: "destructive" });
    } finally {
      setVerifyingQr(false);
    }
  };

  const handleCheckIn = async (membership: Membership) => {
    try {
      const { data, error } = await supabase.functions.invoke('verify-tox-pass', {
        body: { memberNumber: membership.member_number, action: 'checkin' }
      });
      
      if (error) throw error;
      
      if (data.valid) {
        queryClient.invalidateQueries({ queryKey: ['admin-memberships'] });
        toast({ title: "Checked In", description: `Visit #${data.member.visitsCount} recorded` });
        
        // Track Meta Pixel check-in event
        trackCheckIn({
          memberNumber: membership.member_number,
          locationId: membership.location_id || undefined,
          visitCount: data.member.visitsCount,
        });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to check in", variant: "destructive" });
    }
  };

  const handleStageChange = (membership: Membership, newStage: string) => {
    const updates: Partial<Membership> & { id: string } = { 
      id: membership.id, 
      pipeline_stage: newStage 
    };
    
    // Auto-set dates based on stage
    if (newStage === 'tox_pass_activated' && membership.status === 'pending') {
      updates.status = 'active';
    }
    if (newStage === 'payment_processed' && !membership.payment_date) {
      updates.payment_date = new Date().toISOString();
    }
    if (newStage === 'first_treatment_complete' && !membership.first_treatment_date) {
      updates.first_treatment_date = new Date().toISOString();
    }
    
    updateMembershipMutation.mutate(updates);
  };

  const filteredMemberships = memberships.filter(m => 
    (m.customer_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (m.customer_email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (m.member_number || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stageCounts = PIPELINE_STAGES.map(stage => ({
    ...stage,
    count: memberships.filter(m => (m.pipeline_stage || 'lead') === stage.value).length
  }));

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStageBadge = (stage: string | null) => {
    const stageInfo = PIPELINE_STAGES.find(s => s.value === (stage || 'lead'));
    if (!stageInfo) return <Badge variant="outline">Unknown</Badge>;
    return (
      <Badge className={`${stageInfo.color} text-white`}>
        {stageInfo.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Pipeline Overview */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {stageCounts.map((stage, idx) => (
          <Card key={stage.value} className="relative overflow-hidden">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <stage.icon className="w-5 h-5 text-muted-foreground" />
                {idx < stageCounts.length - 1 && (
                  <ArrowRight className="w-4 h-4 text-muted-foreground absolute right-1 top-1/2 -translate-y-1/2" />
                )}
              </div>
              <div className="text-2xl font-bold">{stage.count}</div>
              <div className="text-xs text-muted-foreground truncate">{stage.label}</div>
            </CardContent>
            <div className={`absolute bottom-0 left-0 right-0 h-1 ${stage.color}`} />
          </Card>
        ))}
      </div>

      {/* QR Verification */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <QrCode className="w-5 h-5" />
            QR Code Lookup
          </CardTitle>
          <CardDescription>Scan or enter a member number to verify</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Input
              placeholder="Enter member number (e.g., TTX-XXXXXXXX)"
              value={qrLookupNumber}
              onChange={(e) => setQrLookupNumber(e.target.value)}
              className="max-w-sm"
            />
            <Button onClick={handleVerifyQr} disabled={verifyingQr}>
              <Search className="w-4 h-4 mr-2" />
              {verifyingQr ? "Verifying..." : "Verify"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Members Table */}
      <Card>
        <CardHeader>
          <CardTitle>Membership Pipeline</CardTitle>
          <CardDescription>Track members through the conversion funnel</CardDescription>
          <div className="mt-4">
            <Input
              placeholder="Search by name, email, or member number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-sm"
            />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-center py-8 text-muted-foreground">Loading memberships...</p>
          ) : filteredMemberships.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No memberships found.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Member</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Visits</TableHead>
                    <TableHead>Last Visit</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMemberships.map((membership) => (
                    <TableRow key={membership.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{membership.customer_name}</p>
                          <p className="text-xs text-muted-foreground">{membership.member_number}</p>
                          <p className="text-xs text-muted-foreground">{membership.customer_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {membership.locations ? (
                          <span className="text-sm">{membership.locations.name}</span>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={membership.pipeline_stage || 'lead'}
                          onValueChange={(value) => handleStageChange(membership, value)}
                        >
                          <SelectTrigger className="w-48">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PIPELINE_STAGES.map(stage => (
                              <SelectItem key={stage.value} value={stage.value}>
                                <div className="flex items-center gap-2">
                                  <div className={`w-2 h-2 rounded-full ${stage.color}`} />
                                  {stage.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{membership.visits_count}</Badge>
                      </TableCell>
                      <TableCell>{formatDate(membership.last_visit_at)}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCheckIn(membership)}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Check In
                          </Button>
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setSelectedMember(membership)}
                              >
                                <QrCode className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-md">
                              <DialogHeader>
                                <DialogTitle>Member Details</DialogTitle>
                              </DialogHeader>
                              {selectedMember && (
                                <MemberDetailView 
                                  membership={selectedMember} 
                                  onUpdate={(updates) => updateMembershipMutation.mutate({ id: selectedMember.id, ...updates })}
                                />
                              )}
                            </DialogContent>
                          </Dialog>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Membership</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete {membership.customer_name}'s membership ({membership.member_number})? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => deleteMembershipMutation.mutate(membership.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

interface MemberDetailViewProps {
  membership: Membership;
  onUpdate: (updates: Partial<Membership>) => void;
}

const MemberDetailView = ({ membership, onUpdate }: MemberDetailViewProps) => {
  const [paymentAmount, setPaymentAmount] = useState(membership.payment_amount?.toString() || '');
  const [nextAppointment, setNextAppointment] = useState(membership.next_appointment_date?.split('T')[0] || '');

  return (
    <div className="space-y-6">
      {/* QR Code */}
      <div className="flex justify-center">
        <div className="bg-white p-4 rounded-lg">
          <QRCode value={`TRUETOX:${membership.member_number}`} size={150} />
        </div>
      </div>

      {/* Member Info */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <User className="w-4 h-4 text-muted-foreground" />
          <div>
            <p className="font-medium">{membership.customer_name}</p>
            <p className="text-xs text-muted-foreground">{membership.member_number}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Mail className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm">{membership.customer_email}</span>
        </div>
        {membership.customer_phone && (
          <div className="flex items-center gap-3">
            <Phone className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm">{membership.customer_phone}</span>
          </div>
        )}
        <div className="flex items-center gap-3">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm">Member since {new Date(membership.valid_from).toLocaleDateString()}</span>
        </div>
        {membership.discount_code && (
          <div className="flex items-center gap-3">
            <CreditCard className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-mono bg-muted px-2 py-1 rounded">{membership.discount_code}</span>
          </div>
        )}
      </div>

      {/* Payment Amount */}
      <div>
        <label className="text-sm font-medium">Payment Amount ($)</label>
        <Input
          type="number"
          value={paymentAmount}
          onChange={(e) => setPaymentAmount(e.target.value)}
          placeholder="0.00"
          className="mt-1"
        />
      </div>

      {/* Next Appointment */}
      <div>
        <label className="text-sm font-medium">Next Appointment Date</label>
        <Input
          type="date"
          value={nextAppointment}
          onChange={(e) => setNextAppointment(e.target.value)}
          className="mt-1"
        />
      </div>

      <Button 
        className="w-full"
        onClick={() => onUpdate({
          payment_amount: paymentAmount ? parseFloat(paymentAmount) : null,
          next_appointment_date: nextAppointment ? new Date(nextAppointment).toISOString() : null,
        })}
      >
        Save Changes
      </Button>
    </div>
  );
};
